// Shared engine types + the main<->worker message protocol.
// This file (and everything under engine/) must stay framework-agnostic.

export type ScanMode = 'auto' | 'guide';
export type ExecutionProvider = 'webgpu' | 'wasm';

/** Axis-aligned rect in frame pixels. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Rect normalized to [0,1] relative to the full camera frame. */
export interface NormRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

/** 4 corners, ordered TL, TR, BR, BL, in frame pixels. */
export type Quad = [Point, Point, Point, Point];

export interface Detection {
  box: Rect;
  score: number;
}

export interface OcrRead {
  /** Raw decoded string with pad chars stripped, uppercase. */
  raw: string;
  /** Per-character max-softmax confidences, same length as raw. */
  charConfs: number[];
  region?: { name: string; conf: number } | undefined;
}

export interface FrameTimings {
  detect: number;
  rectify: number;
  ocr: number;
  total: number;
}

export interface PipelineResult {
  frameId: number;
  ep: ExecutionProvider;
  timings: FrameTimings;
  /** Frame size the worker processed (after digital-zoom crop), for overlay math. */
  frameW: number;
  frameH: number;
  detection?: Detection | undefined;
  quad?: Quad | undefined;
  read?: OcrRead | undefined;
}

// ---- messages: main -> worker ----

export interface InitMsg {
  t: 'init';
  detectorUrl: string;
  ocrUrl: string;
  /** Force an EP for debugging (?ep=wasm). */
  forceEp?: ExecutionProvider | undefined;
}

export interface FrameMsg {
  t: 'frame';
  bitmap: ImageBitmap;
  mode: ScanMode;
  /**
   * auto mode: optional digital-zoom crop region (frame-normalized) — detection runs inside it.
   * guide mode: the guide-box ROI (frame-normalized) — OCR runs directly on it.
   */
  roi?: NormRect | undefined;
  /** Enable classic-CV quad rectification before OCR (M4 ladder step 2). */
  rectify: boolean;
  frameId: number;
}

export type MainToWorker = InitMsg | FrameMsg;

// ---- messages: worker -> main ----

export interface ReadyMsg {
  t: 'ready';
  ep: ExecutionProvider;
  threads: number;
  crossOriginIsolated: boolean;
}

export interface ProgressMsg {
  t: 'progress';
  url: string;
  loaded: number;
  total: number;
}

export interface ResultMsg extends PipelineResult {
  t: 'result';
}

export interface ErrorMsg {
  t: 'error';
  stage: 'init' | 'frame';
  message: string;
}

export type WorkerToMain = ReadyMsg | ProgressMsg | ResultMsg | ErrorMsg;

// ---- scan records (history) ----

export interface ScanRecord {
  id?: number;
  text: string;
  region?: string | undefined;
  confidence: number;
  /** epoch ms */
  ts: number;
  /** true when the user hand-corrected the text */
  edited: boolean;
  /** user-starred; every lock is auto-recorded, favorites are the ones that matter */
  favorite: boolean;
}

/** Tunable constants, one object so device-tuning touches a single place (spec §6). */
export const TUNING = {
  /** min mean char confidence to accept a read */
  cMin: 0.85,
  /** min detector score (auto mode) */
  dMin: 0.35,
  /** ring buffer size */
  bufferSize: 10,
  /** reads agreeing on length/pattern required to lock */
  lockMinReads: 5,
  /** weighted vote share each position's winner must hold */
  lockShare: 0.7,
  /** stricter share for the permissive fallback pattern */
  lockShareFallback: 0.8,
  /** reset voting if no accepted read for this long (ms) */
  resetAfterMs: 700,
  /** padding added around the detector box before OCR crop (fraction of box size) */
  cropPad: 0.12,
  /** auto-suggest guide mode below this detector fps */
  guideSuggestFps: 4,
  /** detector box IoU to consider it "the same candidate" (stickiness) */
  stickyIou: 0.3,
} as const;
